import { useId } from "react";
import { CheckCircle2, CircleDot, Plus, X, XCircle } from "lucide-react";

import { OWN_TESTS_ATTR } from "@/anticheat/workArea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  MAX_OWN_TEST_CHARS,
  MAX_OWN_TESTS,
  newOwnTest,
  ownTestCopy,
  type OwnTest,
  type OwnTestResult,
} from "@/lib/ownTests";
import type { Language } from "@/lib/runtimes";
import type { QuestionContract } from "@/lib/runtimes/registry";
import { cn } from "@/lib/utils";

/**
 * FEAT-29 — "Your tests": the candidate's own test cases on a coding question,
 * under the example tests. Up to five rows of an input and an optional
 * expected output; they run with the same "Run tests" button and their results
 * show under the example tests' (`OwnTestResults`).
 *
 * The fields' wrapper carries `OWN_TESTS_ATTR`: on a monitored test a paste
 * there is treated exactly as a paste in the code editor — it lands, is
 * recorded, and the candidate sees the same notice. Without the attribute the
 * page would block Ctrl+C / Ctrl+V in these fields as a blocked shortcut,
 * which flags the attempt of someone copying a value between two rows.
 *
 * Plain textareas, no portal, nothing fetched. All wording is a placeholder
 * (Emilia's).
 */
export function OwnTestsCard({
  tests,
  onChange,
  language,
  contract,
  twisted,
  disabled = false,
}: {
  tests: OwnTest[];
  onChange: (tests: OwnTest[]) => void;
  language: Language;
  contract: QuestionContract;
  /** Cheat-proof challenge: the function returns the twisted "Final output". */
  twisted: boolean;
  /** A Run is in flight: the rows it was started with are not edited under it. */
  disabled?: boolean;
}) {
  const copy = ownTestCopy(language, contract);
  const baseId = useId();
  const full = tests.length >= MAX_OWN_TESTS;
  const patch = (id: string, change: Partial<OwnTest>) =>
    onChange(tests.map((t) => (t.id === id ? { ...t, ...change } : t)));
  return (
    <Card data-testid="own-tests">
      <CardHeader>
        <CardTitle>Your tests</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Try your code on cases of your own. They run with &ldquo;Run
          tests&rdquo; and are never graded. {copy.hint}
        </p>
        {twisted && (
          <p className="text-xs text-muted-foreground" data-testid="own-tests-twist-note">
            This is a cheat-proof challenge: your function returns the{" "}
            <em>Final output</em>, so an expected value must be written in that
            form too.
          </p>
        )}
        <div {...{ [OWN_TESTS_ATTR]: "" }} className="space-y-4">
          {tests.map((t, idx) => {
            const inputId = `${baseId}-in-${t.id}`;
            const expectedId = `${baseId}-exp-${t.id}`;
            return (
              <div
                key={t.id}
                className="space-y-2 rounded-md border border-border p-3"
                data-testid="own-test-row"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium">Your test {idx + 1}</div>
                  <button
                    type="button"
                    onClick={() => onChange(tests.filter((x) => x.id !== t.id))}
                    disabled={disabled}
                    aria-label={`Remove your test ${idx + 1}`}
                    className="-mr-1 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <div>
                    <label htmlFor={inputId} className="text-xs text-muted-foreground">
                      {copy.inputLabel}
                    </label>
                    <textarea
                      id={inputId}
                      value={t.input}
                      onChange={(e) => patch(t.id, { input: e.target.value })}
                      placeholder={copy.inputPlaceholder}
                      maxLength={MAX_OWN_TEST_CHARS}
                      rows={2}
                      spellCheck={false}
                      autoComplete="off"
                      autoCapitalize="off"
                      autoCorrect="off"
                      disabled={disabled}
                      className={FIELD_CLASS}
                    />
                  </div>
                  <div>
                    <label htmlFor={expectedId} className="text-xs text-muted-foreground">
                      {copy.expectedLabel}
                    </label>
                    <textarea
                      id={expectedId}
                      value={t.expected}
                      onChange={(e) => patch(t.id, { expected: e.target.value })}
                      placeholder={copy.expectedPlaceholder}
                      maxLength={MAX_OWN_TEST_CHARS}
                      rows={2}
                      spellCheck={false}
                      autoComplete="off"
                      autoCapitalize="off"
                      autoCorrect="off"
                      disabled={disabled}
                      className={FIELD_CLASS}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange([...tests, newOwnTest()])}
            disabled={full || disabled}
            className="cursor-pointer"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add a test
          </Button>
          {full && (
            <span className="text-xs text-muted-foreground">
              Up to {MAX_OWN_TESTS} tests per question.
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const FIELD_CLASS =
  "mt-1 block w-full resize-y rounded border border-input bg-background p-2 font-mono text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60";

/** The own cases' results, under the example tests' in the run panel. A case
 *  with nothing expected is neither passed nor failed — it shows its output.
 *  The output is shown on a PASSED row too: seeing what the code returned is
 *  the point of an own case. */
export function OwnTestResults({ results }: { results: OwnTestResult[] }) {
  if (results.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="own-test-results">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Your tests
      </div>
      {results.map((r, idx) => {
        const state = ownResultState(r);
        return (
          <Alert
            key={idx}
            data-state={state}
            className={cn(
              "border",
              state === "passed" && "border-success/40 bg-success/5",
              (state === "failed" || state === "error") &&
                "border-destructive/40 bg-destructive/5",
              (state === "ran" || state === "skipped") && "border-border bg-muted/30",
            )}
          >
            <div className="flex items-start gap-3">
              {state === "passed" ? (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden="true" />
              ) : state === "failed" || state === "error" ? (
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
              ) : (
                <CircleDot
                  className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">Your test {idx + 1}</div>
                <AlertDescription className="space-y-2 text-xs">
                  <div
                    className={cn(
                      state === "passed" && "text-success",
                      (state === "failed" || state === "error") && "text-destructive",
                      (state === "ran" || state === "skipped") && "text-muted-foreground",
                    )}
                  >
                    {STATE_LABEL[state]}
                    {state === "skipped" && r.note ? ` — ${r.note}` : ""}
                  </div>
                  {r.input.trim() !== "" && <Block label="Input" text={r.input} />}
                  {r.output !== undefined && r.output !== "" && (
                    <Block label="Output" text={r.output} />
                  )}
                  {state === "ran" && (r.output === undefined || r.output === "") && (
                    <div className="text-muted-foreground">No output.</div>
                  )}
                  {r.error && <Block label="Error" text={r.error} />}
                </AlertDescription>
              </div>
            </div>
          </Alert>
        );
      })}
    </div>
  );
}

type OwnResultState = "passed" | "failed" | "ran" | "error" | "skipped";

const STATE_LABEL: Record<OwnResultState, string> = {
  passed: "Passed",
  failed: "Failed",
  ran: "Ran — nothing expected",
  error: "Error",
  skipped: "Not run",
};

export function ownResultState(r: OwnTestResult): OwnResultState {
  if (r.note) return "skipped";
  if (r.error) return "error";
  if (r.passed === true) return "passed";
  if (r.passed === false) return "failed";
  return "ran";
}

function Block({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <div className="text-muted-foreground">{label}</div>
      <pre className="mt-1 max-h-48 overflow-auto rounded bg-secondary p-2 font-mono text-xs">
        {text}
      </pre>
    </div>
  );
}
